// Телеметрия вкладки. До неё падение клиента у саппорта не оставляло никакого
// следа: репортинга не было вовсе, а жалоба «клиент вылетает при массовой
// подготовке пушей» не воспроизводится на пустом аккаунте. Шлём в шлюз два
// вида записей — падение (ошибка рендера / необработанное исключение) и
// снимок памяти со счётчиками стора. Обе попадают в журнал дашборда
// «Нагрузка» (app_worker_alerts, worker_id = "client"), так что смотреть их
// можно там же, где события воркеров.

export interface ClientTelemetryCounters {
  /** Чатов в сторе (весь сайдбар, все папки). */
  chats: number;
  /** Чатов, чья лента сообщений сейчас в памяти. */
  feeds: number;
  /** Сообщений во всех лентах суммарно. */
  messages: number;
  /** Записей в кэше переводов. */
  translations: number;
  /** Незаконченных черновиков. */
  drafts: number;
}

export interface ClientTelemetrySource {
  (): ClientTelemetryCounters & { accountId?: string | undefined };
}

interface MemoryUsage {
  usedMb: number;
  limitMb: number;
  ratio: number;
}

// Chrome-only, в других браузерах просто не шлём блок памяти.
const readMemory = (): MemoryUsage | undefined => {
  const memory = (
    performance as Performance & {
      memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number };
    }
  ).memory;
  if (!memory || !memory.jsHeapSizeLimit) {
    return undefined;
  }
  const usedMb = Math.round(memory.usedJSHeapSize / (1024 * 1024));
  const limitMb = Math.round(memory.jsHeapSizeLimit / (1024 * 1024));
  return {
    usedMb,
    limitMb,
    ratio: Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100) / 100
  };
};

const SNAPSHOT_INTERVAL_MS = 60_000;
// Базовая линия — раз в час и только когда память уже заметно занята: журнал
// «Нагрузки» общий с событиями воркеров, засорять его нечем.
const IDLE_SNAPSHOT_MS = 60 * 60_000;
const REPORTABLE_RATIO = 0.5;
// Под давлением памяти — часто: интересен именно рост перед падением.
const PRESSURE_SNAPSHOT_MS = 2 * 60_000;
const PRESSURE_RATIO = 0.75;
// Петля ошибок не должна превратиться в петлю запросов.
const MAX_CRASHES_PER_SESSION = 5;

// Шум, который браузер сообщает через window.onerror, но который падением не
// является. Журнал «Нагрузки» из-за него показывал «Вкладка саппорта:
// падение» там, где вкладка просто жива и работает (память 24 из 4192 МБ), и
// настоящие падения тонули среди этих строк.
// - ResizeObserver loop … — штатное уведомление браузера о том, что колбэк
//   наблюдателя изменил геометрию и замер перенесён на следующий кадр.
// - Script error. — ошибка из стороннего origin без деталей, диагностировать
//   по ней нечего.
const IGNORED_ERROR_PATTERNS = [
  "ResizeObserver loop",
  "Script error."
];

const isNoiseError = (message: string): boolean =>
  IGNORED_ERROR_PATTERNS.some((pattern) => message.includes(pattern));

let source: ClientTelemetrySource | null = null;
let lastSnapshotAt = 0;
let crashesSent = 0;
const reportedCrashes = new Set<string>();

const post = (body: Record<string, unknown>): void => {
  try {
    void fetch("/api/client-health", {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body)
    }).catch(() => {
      // Телеметрия никогда не должна мешать работе клиента.
    });
  } catch {
    // ignore
  }
};

export const reportClientCrash = (message: string, stack?: string): void => {
  if (isNoiseError(message)) {
    return;
  }
  const key = `${message}::${(stack ?? "").slice(0, 200)}`;
  if (crashesSent >= MAX_CRASHES_PER_SESSION || reportedCrashes.has(key)) {
    return;
  }
  reportedCrashes.add(key);
  crashesSent += 1;
  const counters = source?.();
  post({
    kind: "crash",
    message: message.slice(0, 500),
    ...(stack ? { stack: stack.slice(0, 2000) } : {}),
    ...(counters ? { counters, accountId: counters.accountId } : {}),
    memory: readMemory(),
    url: window.location.pathname
  });
};

const sendSnapshot = (reason: "interval" | "pressure" | "hidden"): void => {
  const counters = source?.();
  if (!counters) {
    return;
  }
  lastSnapshotAt = Date.now();
  const { accountId, ...rest } = counters;
  post({
    kind: "snapshot",
    reason,
    counters: rest,
    ...(accountId ? { accountId } : {}),
    memory: readMemory()
  });
};

export const initClientTelemetry = (collect: ClientTelemetrySource): (() => void) => {
  source = collect;

  const onError = (event: ErrorEvent): void => {
    reportClientCrash(event.message || "window error", event.error?.stack);
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    const reason = event.reason;
    reportClientCrash(
      reason instanceof Error ? reason.message : String(reason ?? "unhandled rejection"),
      reason instanceof Error ? reason.stack : undefined
    );
  };

  const timer = window.setInterval(() => {
    const memory = readMemory();
    // Браузер без performance.memory (не Chrome) сам о себе не расскажет —
    // от него шлём только падения.
    if (!memory || memory.ratio < REPORTABLE_RATIO) {
      return;
    }
    const underPressure = memory.ratio >= PRESSURE_RATIO;
    const due = Date.now() - lastSnapshotAt;
    if (underPressure ? due >= PRESSURE_SNAPSHOT_MS : due >= IDLE_SNAPSHOT_MS) {
      sendSnapshot(underPressure ? "pressure" : "interval");
    }
  }, SNAPSHOT_INTERVAL_MS);

  // Вкладку закрывают/сворачивают под давлением памяти — это самый близкий к
  // падению момент, который мы вообще можем застать.
  const onVisibility = (): void => {
    if (document.visibilityState !== "hidden") {
      return;
    }
    const memory = readMemory();
    if (memory && memory.ratio >= PRESSURE_RATIO) {
      sendSnapshot("hidden");
    }
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    window.clearInterval(timer);
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    document.removeEventListener("visibilitychange", onVisibility);
    source = null;
  };
};
